import { useMemo } from 'react';
import { cloneDeep } from 'lodash';
import {
  buildPolicyFundingSourceFromGenworthOption,
  calcClientDerivatives,
  type GenworthRateActionLetterResults,
  type GenworthRateActionOption,
} from '@shared';

export interface RateActionProjection {
  /** Index in `results.options`, or `null` for the synthetic "true original" row. */
  optionIndex: number | null;
  /** The derived policy off the cloned client — read projection fields off this. */
  derived: PolicyFundingSource;
}

export interface UseRateActionProjectionsParams {
  results: GenworthRateActionLetterResults | null;
  client: Client | null | undefined;
  requestId: string;
}

// Mirrors `createQuoteWithROIandProjectedCoverage` in shop-insurance: preview a candidate
// policy that the client doesn't actually have, by grafting it onto a clone and running
// the full derivation. Partner is null — joint policies are out of scope (plan §4.4).
export function useRateActionProjections({
  results,
  client,
  requestId,
}: UseRateActionProjectionsParams): RateActionProjection[] {
  return useMemo(() => {
    if (!results || !client) return [];
    // On-claim clients break the projection model: the calc engine treats
    // clientHasStartedLtc as "premiums waived" and collapses the premium window to
    // zero. Rather than ship misleading $0 / — / 0% rows, the Projections tab
    // surfaces a banner; skip the per-option derivation work entirely here.
    if (client.intakeSurvey?.clientHasStartedLtc) return [];

    const { metadata, options } = results;
    const keepCurrent = options.find(o => o.optionType === 'KEEP_CURRENT');

    // Per-option clone so each option's projection is isolated; shared funding-source
    // state across the cloned client would otherwise let one graft influence another.
    const projections: RateActionProjection[] = [];

    // Synthetic "true original" — KEEP_CURRENT benefits at the pre-increase premium. Skip
    // entirely if `premiumBeforeIncrease` wasn't extracted: a null premium silently zeros out
    // the whole projection row, which is the anchor users care about — better to render —
    // and leave the user a banner than to display a misleading "no cost" projection.
    if (keepCurrent && metadata.premiumBeforeIncrease != null) {
      const derived = deriveOptionPolicy({
        option: keepCurrent,
        metadata,
        client,
        tempIdSuffix: 'true-original',
        annualPremiumOverride: metadata.premiumBeforeIncrease,
      });
      if (derived) {
        projections.push({ optionIndex: null, derived });
      }
    }

    options.forEach((option, optionIndex) => {
      // Nonforfeiture / "Optional Limited Benefit" is modelable: $0 premium, 0 inflation,
      // fixed benefit pool. The calc engine handles those values fine, and showing the
      // projection lets users compare paid-up against the other reduced-coverage options.
      const derived = deriveOptionPolicy({
        option,
        metadata,
        client,
        tempIdSuffix: `opt-${optionIndex}`,
      });
      if (derived) {
        projections.push({ optionIndex, derived });
      }
    });

    return projections;
  }, [results, client, requestId]);
}

function deriveOptionPolicy(args: {
  option: GenworthRateActionOption;
  metadata: GenworthRateActionLetterResults['metadata'];
  client: Client;
  tempIdSuffix: string;
  annualPremiumOverride?: number | null;
}): PolicyFundingSource | null {
  const { option, metadata, client, tempIdSuffix, annualPremiumOverride } = args;
  const tempId = `temp-rate-action-${tempIdSuffix}`;
  const built = buildPolicyFundingSourceFromGenworthOption(option, metadata, {
    annualPremiumOverride,
  });

  const clone = cloneDeep(client);
  clone.multipleFundingSources = clone.multipleFundingSources ?? ({} as Client['multipleFundingSources']);
  clone.multipleFundingSources.ltcPolicy = {
    ...clone.multipleFundingSources.ltcPolicy,
    [tempId]: built,
  };

  const derivedClient = calcClientDerivatives(clone, null);
  return derivedClient.multipleFundingSources.ltcPolicy[tempId] ?? null;
}
